import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_neutral_boho_003',
  name: 'Neutral Boho',
  occasionType: 'BABY_SHOWER',
  description: 'A warm and earthy baby shower invitation featuring dried pampas grass, cream tones, and a soft minimalist rainbow.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'boho', 'minimal', 'pampas'],
  colorScheme: { primary: '#b45309', accent: '#d97706', bg: '#fef3c7' },
};

export function NeutralBoho({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Clara Sterling';
  const hostedBy = baby.hostedBy || data.hostName || 'The Sterling Family';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'December 05, 2026';
  let dayOfWeek = 'Saturday';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '01:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Greenhouse Pavilion';
  const venueAddress = baby.venueAddress || data.eventVenue || '321 Earthy Way, Santa Barbara, CA';
  
  const rsvpName = baby.rsvpName || 'Nora';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 014-9988';
  const message = baby.message || data.customMessage || 'We can’t wait to welcome our little blessing. Please join us in showering the mother-to-be!';
  const dressCode = baby.dressCode || '';

  return (
    <div className="min-h-screen bg-[#faf6f0] py-12 px-4 flex flex-col items-center justify-start font-sans relative overflow-hidden">
      {/* Import Google Fonts */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Great+Vibes&family=Source+Sans+3:wght@300;400;600&display=swap');
        .font-serif-boho { font-family: 'DM Serif Display', serif; }
        .font-cursive-boho { font-family: 'Great Vibes', cursive; }
        .font-sans-boho { font-family: 'Source Sans 3', sans-serif; }
        .boho-card {
          box-shadow: 0 20px 40px -15px rgba(180, 83, 9, 0.08);
          background-image: radial-gradient(#d4b28c 0.5px, transparent 0.5px), radial-gradient(#d4b28c 0.5px, #fffaf4 0.5px);
          background-size: 20px 20px;
          background-position: 0 0, 10px 10px;
          background-opacity: 0.15;
        }
      `}} />

      {/* Main Card */}
      <div className="w-full max-w-[480px] aspect-[9/16] bg-[#fffaf4] rounded-[28px] shadow-2xl p-8 relative flex flex-col justify-between border-[10px] border-[#f3ebde] overflow-hidden">
        
        {/* Soft Linen Texture Overlays */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none boho-card"></div>

        {/* Content Container */}
        <div className="flex-grow flex flex-col justify-center items-center text-center relative z-10 py-2 px-2">
          
          {/* Minimalist Boho Rainbow SVG */}
          <div className="w-24 h-16 mb-6 opacity-85">
            <svg viewBox="0 0 100 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              {/* Outer arch */}
              <path d="M10,50 A40,40 0 0,1 90,50" stroke="#c084fc" strokeWidth="4" strokeLinecap="round" opacity="0.4" />
              {/* Mid arch */}
              <path d="M22,50 A28,28 0 0,1 78,50" stroke="#f472b6" strokeWidth="4" strokeLinecap="round" opacity="0.5" />
              {/* Inner arch */}
              <path d="M34,50 A16,16 0 0,1 66,50" stroke="#fbbf24" strokeWidth="4" strokeLinecap="round" opacity="0.6" />
              {/* Innermost arch */}
              <path d="M46,50 A4,4 0 0,1 54,50" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
              {/* Dried grass sprays around rainbow */}
              <path d="M15,48 L8,42" stroke="#d4b28c" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M85,48 L92,42" stroke="#d4b28c" strokeWidth="1.5" strokeLinecap="round" />
              {/* Soft stars */}
              <path d="M50,15 L51,18 L54,18 L52,19 L53,22 L50,20 L47,22 L48,19 L46,18 L49,18 Z" fill="#d4b28c" />
            </svg>
          </div>

          <span className="font-sans-boho text-[10px] tracking-[0.25em] text-[#854d0e] uppercase font-semibold mb-3">
            A Little One Is On The Way
          </span>

          <h2 className="font-serif-boho text-4xl text-[#78350f] tracking-wide font-normal mb-2 leading-none">
            BABY SHOWER
          </h2>

          <span className="font-sans-boho text-xs text-[#a16207] italic mb-6">
            honoring
          </span>

          <h1 className="font-cursive-boho text-5xl text-[#b45309] leading-none mb-6 font-bold py-1">
            {motherName}
          </h1>

          <div className="w-10 h-px bg-[#d4b28c] mb-6"></div>

          <p className="font-sans-boho text-xs tracking-wider text-stone-500 mb-6 leading-relaxed">
            Hosted by <br />
            <span className="font-semibold text-stone-800">{hostedBy}</span>
          </p>

          {/* Date & Time block */}
          <div className="flex flex-col items-center mb-6">
            <span className="font-serif-boho text-xl text-[#78350f]">
              {dayOfWeek}, {formattedDate}
            </span>
            <span className="font-sans-boho text-xs tracking-widest text-[#b45309] uppercase font-semibold mt-1.5">
              At {eventTime}
            </span>
          </div>

          {/* Venue block */}
          <div className="flex flex-col items-center mb-6 max-w-[280px]">
            <span className="font-serif-boho text-sm text-stone-800 mb-1">
              {venueName}
            </span>
            <span className="font-sans-boho text-xs text-stone-500 leading-normal">
              {venueAddress}
            </span>
          </div>

          {/* Dress Code */}
          {dressCode && (
            <div className="mb-6 px-4 py-1.5 bg-[#fef3c7]/60 rounded-full border border-[#fde68a]">
              <span className="font-sans-boho text-[10px] tracking-wider text-[#b45309] uppercase font-semibold">
                Dress Code: {dressCode}
              </span>
            </div>
          )}

          {/* Message */}
          {message && (
            <p className="font-serif-boho text-xs text-stone-500 italic leading-relaxed max-w-[300px] mb-6">
              "{message}"
            </p>
          )}

          {/* RSVP Button inside Card */}
          <div className="mt-2 w-full flex justify-center">
            <button
              type="button"
              onClick={() => setShowRsvpModal(true)}
              className="inline-flex items-center justify-center px-8 py-3 bg-[#78350f] hover:bg-[#5f280a] text-white font-bold text-sm uppercase tracking-widest rounded-full shadow-md transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
            >
              RSVP Now
            </button>
          </div>

        </div>
      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          {/* Modal Container */}
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: NeutralBoho });
